import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, RefreshCw, TriangleAlert } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Confirmar } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, post } from '../../nucleo/api';
import { dataHora } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { AvisoConector, useEstadoCanais } from './dados';
import { EditorTemplate, situacaoDoTemplate } from './EditorTemplate';
import type { TemplateItem } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

/**
 * M3.3: templates da Meta, sincronizados por job (a cada 6 horas e sob demanda). U14 F4: também criados aqui, mandados para
 * aprovação (Meta direta ou Zernio) e acompanhados pela situação que a Meta devolve.
 */
export function Templates() {
  const { t } = useTranslation('conversas');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const estado = useEstadoCanais();
  // U14 F4: o modelo aberto no editor ('novo' para um modelo novo).
  const [aberto, setAberto] = useState<TemplateItem | 'novo' | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(
    () => () => {
      for (const x of timer.current) clearTimeout(x);
    },
    [],
  );
  const conectado = !!estado.data?.whatsapp;
  const atualizar = async () => {
    await qc.invalidateQueries({ queryKey: ['/conversas/templates'] });
    await qc.invalidateQueries({ queryKey: ['conversas', 'templates'] });
  };
  /** Atualiza a lista agora e de novo em alguns segundos (a sincronização e o envio para aprovação correm em job). */
  const atualizarDepois = (...ms: number[]) => {
    for (const espera of ms) timer.current.push(setTimeout(() => void atualizar(), espera));
  };
  // Fase 2 (W16): excluir o template do MalgoOS (na Meta continua), com lixeira de 30 dias.
  const [excluindo, setExcluindo] = useState(false);
  const atual = aberto && aberto !== 'novo' ? aberto : null;
  const excluir = useMutation({
    mutationFn: (id: string) => del(`/conversas/templates/${id}`),
    onSuccess: async () => {
      setExcluindo(false);
      setAberto(null);
      toast.sucesso(t('templates.excluido'));
      await atualizar();
    },
    onError: (e) => {
      setExcluindo(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  const sincronizar = useMutation({
    mutationFn: () => post('/conversas/templates/sincronizar'),
    onSuccess: () => {
      toast.info(t('templates.sincronizando'));
      atualizarDepois(5000);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const colunas: Coluna<TemplateItem>[] = [
    { chave: 'nome', rotulo: t('templates.nome'), principal: true, mono: true },
    { chave: 'corpo', rotulo: t('templates.corpo'), render: (x) => <TextoCortado linhas={2} className="whitespace-pre-wrap">{x.corpo}</TextoCortado> },
    { chave: 'idioma', rotulo: t('templates.idioma'), mono: true },
    {
      chave: 'categoria',
      rotulo: t('templates.categoria'),
      render: (x) => (
        <span className="inline-flex items-center gap-1">
          <Selo>{t(`templates.categorias.${x.categoria}`)}</Selo>
          {(x.categoria_prevista || x.categoria_anterior) && <TriangleAlert className="size-4 text-laranja" aria-label={x.categoria_prevista ? t('modelos.categoriaPrevista', { nova: t(`templates.categorias.${x.categoria_prevista}`) }) : undefined} />}
        </span>
      ),
    },
    {
      chave: 'status',
      rotulo: t('templates.status'),
      render: (x) => {
        const sit = situacaoDoTemplate(x);
        return <Selo cor={sit.cor}>{t(`modelos.situacoes.${sit.chave}`, { defaultValue: sit.chave })}</Selo>;
      },
    },
    { chave: 'origem', rotulo: t('modelos.origem'), oculta: true, render: (x) => t(`modelos.origens.${x.origem}`) },
    { chave: 'sincronizado_em', rotulo: t('templates.sincronizadoEm'), mono: true, oculta: true, render: (x) => dataHora(x.sincronizado_em) },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {estado.data && !conectado && <AvisoConector texto={t('aviso.templates')} />}
      {/* Val5 (29/10): criar e editar o modelo pedem `conversas.configurar`; sincronizar e enviar à aprovação, `conversas.templates`. */}
      {(s.pode('conversas.configurar') || s.pode('conversas.templates') || s.pode('conversas.excluir')) && (
        <AcaoPrimaria>
          {s.pode('conversas.excluir') && <BotaoLixeira entidades={['conversas.template']} />}
          {s.pode('conversas.templates') && (
            <Botao icone={<RefreshCw className="size-4" />} disabled={!conectado} title={conectado ? undefined : t('acoes.conectorOff')} carregando={sincronizar.isPending} onClick={() => sincronizar.mutate()}>
              {t('acoes.sincronizar')}
            </Botao>
          )}
          {s.pode('conversas.configurar') && (
            <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
              {t('modelos.novo')}
            </Botao>
          )}
        </AcaoPrimaria>
      )}
      <Tabela<TemplateItem> tela="conversas.templates" caminho="/conversas/templates" colunas={colunas} aoClicar={(x) => setAberto(x)} vazio={<Vazio texto={t('templates.vazio')} />} />
      {aberto && (
        <EditorTemplate
          key={atual?.id ?? 'novo'}
          item={atual}
          aberto
          aoFechar={() => setAberto(null)}
          podeEditar={s.pode('conversas.configurar')}
          podeExcluir={s.pode('conversas.excluir')}
          conectado={conectado}
          aoMudar={async () => {
            await atualizar();
            atualizarDepois(3000, 10000);
          }}
          aoExcluir={() => setExcluindo(true)}
        />
      )}
      <Confirmar aberto={excluindo} aoFechar={() => setExcluindo(false)} aoConfirmar={() => atual && excluir.mutate(atual.id)} carregando={excluir.isPending} titulo={t('templates.excluirTitulo')} texto={t('templates.excluirTexto')} perigoso />
    </div>
  );
}
